import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ArrowRight, Search } from 'lucide-react';

export const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      category: "General",
      questions: [
        {
          question: "What is TrustBridge Technologies?",
          answer: "TrustBridge Technologies is a financial technology company that provides transaction infrastructure for businesses in Liberia and Africa. We offer two main products: FlowLedger for transaction tracking and SIMVault for secure SIM hosting."
        },
        {
          question: "Who can use TrustBridge services?",
          answer: "Our services are designed for businesses of all sizes in Liberia and Africa, including local merchants, diaspora entrepreneurs running businesses remotely, and large enterprises needing robust transaction infrastructure."
        },
        {
          question: "Is my data secure with TrustBridge?",
          answer: "Yes, we use bank-level security measures including 256-bit encryption, secure data centers, and strict access controls. Your data is protected 24/7 with continuous monitoring and regular security audits."
        }
      ]
    },
    {
      category: "FlowLedger",
      questions: [
        {
          question: "How does FlowLedger track transactions?",
          answer: "FlowLedger automatically captures and parses SMS messages from mobile money providers. Each transaction is logged, categorized, and made available in your dashboard in real-time."
        },
        {
          question: "Which mobile money providers are supported?",
          answer: "FlowLedger currently supports all major mobile money providers in Liberia including Orange Money, Lonestar MTN Mobile Money, and others. We're continuously adding support for more providers."
        },
        {
          question: "Can I export my transaction data?",
          answer: "Yes, FlowLedger allows you to export your transaction history in various formats including CSV and PDF. This is useful for accounting, reconciliation, and reporting purposes."
        },
        {
          question: "What happens if I miss a transaction?",
          answer: "FlowLedger captures all SMS messages in real-time, so you won't miss any transactions. Even if you're offline, transactions are queued and synced when you reconnect."
        }
      ]
    },
    {
      category: "SIMVault",
      questions: [
        {
          question: "What is SIM hosting?",
          answer: "SIM hosting is a service where your mobile money SIM cards are securely stored in our monitored facility. You can access all messages and activity remotely through our dashboard, without physical access to the SIM."
        },
        {
          question: "How secure is the SIMVault facility?",
          answer: "Our facility features 24/7 video surveillance, biometric access control, climate control, fire suppression systems, and redundant power. All data transmission is encrypted end-to-end."
        },
        {
          question: "Can I access my SIM from abroad?",
          answer: "Yes, that's one of the main benefits of SIMVault. You can monitor your SIM cards, view messages, and manage your accounts from anywhere in the world through our secure dashboard."
        },
        {
          question: "What happens to my SIM messages?",
          answer: "All incoming messages to your hosted SIM are instantly forwarded to your dashboard. You can also set up email or SMS forwarding to receive messages on your personal device."
        },
        {
          question: "How do I deposit my SIM for hosting?",
          answer: "Contact us to schedule a secure handoff at our facility. We'll register your SIM, verify ownership, and activate monitoring. You'll have immediate access through your dashboard."
        }
      ]
    },
    {
      category: "Billing & Support",
      questions: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept various payment methods including bank transfers, mobile money, and international payments. Enterprise customers can arrange custom billing arrangements."
        },
        {
          question: "Is there a free trial?",
          answer: "Yes, FlowLedger offers a 14-day free trial for all plans. This allows you to test all features before committing. SIMVault offers demo sessions to understand the service."
        },
        {
          question: "How do I contact support?",
          answer: "You can reach our support team through the Contact page, by email at support@trustbridgeflow.com, or by phone. Professional and Enterprise plans include priority support with faster response times."
        },
        {
          question: "Can I cancel my subscription?",
          answer: "Yes, you can cancel your subscription at any time. Your data will be available for export for 30 days after cancellation. For SIMVault, we'll arrange secure return of your SIM cards."
        }
      ]
    }
  ];

  const filteredFaqs = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block animate-fade-in">
              Support
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6 animate-fade-in-up">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10 animate-fade-in-up delay-100">
              Find answers to common questions about our products and services.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto animate-fade-in-up delay-200">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-12 pr-4 rounded-full border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                data-testid="faq-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-20 lg:pb-28 bg-white" data-testid="faq-content">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((category, catIndex) => (
                <div key={catIndex} className="mb-12" data-testid={`faq-category-${catIndex}`}>
                  <h2 className="text-xl font-semibold text-slate-900 mb-6">
                    {category.category}
                  </h2>
                  
                  <Accordion type="single" collapsible className="space-y-4">
                    {category.questions.map((faq, faqIndex) => (
                      <AccordionItem 
                        key={faqIndex} 
                        value={`${catIndex}-${faqIndex}`}
                        className="bg-slate-50 rounded-xl border-none px-6"
                        data-testid={`faq-item-${catIndex}-${faqIndex}`}
                      >
                        <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 pb-5">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-500">No questions found matching "{searchQuery}"</p>
                <Button 
                  variant="ghost" 
                  onClick={() => setSearchQuery('')}
                  className="mt-4"
                  data-testid="faq-clear-search"
                >
                  Clear search
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="section-padding bg-slate-50" data-testid="faq-cta">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-slate-600 mb-8">
              Can't find what you're looking for? Our team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="faq-contact-btn">
                  Contact Support
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact?type=demo">
                <Button variant="outline" className="btn-secondary" data-testid="faq-demo-btn">
                  Request Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
